import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useBrand } from "@/contexts/BrandContext";
import { useSuperAdmin } from "@/hooks/useSuperAdmin";
import { COPILOT_HOME_SWITCH_BRAND, COPILOT_HOME_SWITCH_KEY, copilotHomeAvailable, isCopilotHomeSwitchOn } from "@/lib/copilot-home";

export async function fetchCopilotHomeSwitch(): Promise<boolean> {
  const { data, error } = await supabase
    .from("platform_settings")
    .select("value")
    .eq("key", COPILOT_HOME_SWITCH_KEY)
    .eq("brand", COPILOT_HOME_SWITCH_BRAND)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return isCopilotHomeSwitchOn(data?.value);
}

export function useCopilotHome(): { enabled: boolean; loading: boolean } {
  const brand = useBrand();
  const { userRole } = useAuth();
  const { isSuperAdmin, loading: superAdminLoading } = useSuperAdmin();
  const active = copilotHomeAvailable(brand.id);
  const isWorkspaceAdmin = userRole === "admin";
  const { data, isLoading } = useQuery({
    queryKey: ["copilot-home-switch"],
    queryFn: fetchCopilotHomeSwitch,
    enabled: active,
    staleTime: 60 * 1000,
    retry: 1,
  });
  const eligible = isWorkspaceAdmin || isSuperAdmin;
  return {
    enabled: active && data === true && eligible,
    loading: active && (isLoading || (!isWorkspaceAdmin && superAdminLoading)),
  };
}
